import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, Filter, TrendingDown } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_FUNNEL_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, AttributionBanner, EmptyState, StatTile, money, num, pct,
    resolveDateRange,
    StatusPill, isDelivering,
    StatusFilterSelect,
} from "./adReportKit";

const STEP_COLOURS = [
    "bg-blue-500", "bg-indigo-500", "bg-violet-500",
    "bg-fuchsia-500", "bg-amber-500", "bg-emerald-500",
];

/** Horizontal funnel: each step's width is its share of the original leads. */
const Funnel = ({ steps, compact = false }) => (
    <div className={cn("space-y-1", compact && "space-y-0.5")}>
        {steps.map((st, i) => (
            <div key={st.key} className="flex items-center gap-2">
                {!compact && (
                    <span className="text-[11px] text-muted-foreground w-20 shrink-0">{st.label}</span>
                )}
                <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden min-w-[40px]">
                    <div className={cn("h-full rounded-sm transition-all", STEP_COLOURS[i])}
                        style={{ width: `${Math.max(st.of_leads_pct || 0, st.count > 0 ? 1.5 : 0)}%` }}
                        title={`${st.label}: ${st.count} (${st.of_leads_pct}% of leads)`} />
                </div>
                <span className="text-[11px] font-medium text-foreground w-10 text-right tabular-nums shrink-0">
                    {st.count}
                </span>
                {!compact && (
                    <span className="text-[11px] w-16 text-right shrink-0 tabular-nums">
                        {st.drop_pct == null ? (
                            <span className="text-muted-foreground">—</span>
                        ) : (
                            <span className={cn(
                                st.drop_pct >= 50 ? "text-red-600 dark:text-red-400 font-medium"
                                    : st.drop_pct >= 30 ? "text-amber-600 dark:text-amber-400"
                                        : "text-muted-foreground")}>
                                −{st.drop_pct}%
                            </span>
                        )}
                    </span>
                )}
            </div>
        ))}
    </div>
);

export default function AdFunnelReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_fn_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_fn_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_fn_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_fn_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_fn_status", "");
    const [level, setLevel] = usePersistedState("ad_fn_level", "ad");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams({ level });
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        fetch(`${AD_FUNNEL_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load funnel");
            })
            .catch(() => ToastMsg("error", "Failed to load funnel"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, level, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const rows = data?.data || [];
    const s = data?.summary;
    const wired = data?.attribution?.is_wired;
    const worstStep = s?.steps?.filter((x) => x.drop_pct != null)
        .sort((a, b) => b.drop_pct - a.drop_pct)[0];

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Filter className="h-6 w-6 text-primary" />
                            Funnel by Ad
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Where each ad's leads stop moving — and which ads actually produce demo-ready buyers.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <select value={level} onChange={(e) => setLevel(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="ad">Per ad</option>
                        <option value="campaign">Per campaign</option>
                    </select>
                </AdFilterBar>

                <AttributionBanner attribution={data?.attribution} />

                {wired && s && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatTile tone="leads" label="Attributed leads" value={num(s.leads)}
                                sub={`across ${s.entities} ${level === "ad" ? "ads" : "campaigns"}`} />
                            <StatTile label="Reached a demo" value={pct(s.demo_rate, 1)} />
                            <StatTile label="Won" value={num(s.won)} sub={`${pct(s.win_rate, 1)} win rate`} />
                            <StatTile tone={worstStep && worstStep.drop_pct >= 50 ? "bad" : "default"}
                                label="Biggest drop-off"
                                value={worstStep ? `−${worstStep.drop_pct}%` : "—"}
                                sub={worstStep ? `at ${worstStep.label}` : null} />
                        </div>

                        <div className="rounded-xl border border-border bg-card p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-sm font-semibold text-foreground">Overall funnel</h2>
                                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    <TrendingDown className="h-3 w-3" /> drop from previous step
                                </span>
                            </div>
                            <Funnel steps={s.steps} />
                        </div>
                    </>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && wired && rows.length === 0 && (
                    <EmptyState title="No attributed leads in this period" />
                )}

                {!loading && wired && rows.length > 0 && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {rows.map((row) => (
                            <div key={row.id}
                                className={cn("rounded-xl border border-border bg-card p-4 space-y-3",
                                    !isDelivering(row.effective_status) && "opacity-70")}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-semibold text-foreground truncate" title={row.name}>
                                                {row.name}
                                            </p>
                                            <StatusPill status={row.effective_status} compact />
                                        </div>
                                        <p className="text-[11px] text-muted-foreground">
                                            {num(row.leads)} leads · {money(row.spend)} spend · {money(row.cpl)} CPL
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={cn("text-sm font-bold tabular-nums",
                                            row.win_rate >= 10 ? "text-emerald-600 dark:text-emerald-400" : "text-foreground")}>
                                            {pct(row.win_rate, 1)}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground">win rate</p>
                                    </div>
                                </div>
                                <Funnel steps={row.steps} />
                                <div className="flex justify-between text-[11px] text-muted-foreground pt-1 border-t border-border">
                                    <span>{pct(row.demo_rate, 1)} reached a demo</span>
                                    <span>{money(row.cac)} per win</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && wired && rows.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                        A lead sitting at a later stage counts as having passed the earlier ones, because
                        the status log does not always record every hop — dragging a card from New Lead
                        straight to Proposal leaves no Contacted row behind.
                    </p>
                )}
            </div>
        </ContainerCard>
    );
}
